/**
 * Allocation-rules API (TMMAPP-0017, design §6.7 / TMMAPI-0010) — generated wire types in,
 * normalized domain out. Targets are sent with `sortOrder = index`; empty optional strings are not
 * sent. A patch sends only the keys that changed; an omitted key keeps the stored value, and a cleared
 * filter or transfer account is `null` (TMMAPI-0169, TMMAPP-0136 item 10). The caller
 * refetches after a write rather than trusting the answer as the new list (reorder still returns
 * the list).
 */
import { request } from './client';
import { backendFields } from './errorDetails';
import { isApiError } from './errors';
import type {
  AllocationRuleCreateRequest,
  AllocationRuleDryRunMatch as WireDryRunMatch,
  AllocationRuleDryRunResponse,
  AllocationRulePatchRequest,
  AllocationRuleResponse,
  AllocationRuleTargetRequest,
  AllocationRuleTargetView as WireTarget,
} from './generated';

export type RuleTargetMode = 'FIXED' | 'PERCENT';

export interface RuleTarget {
  id: number;
  envelopeId: number;
  categoryId: number | null;
  mode: RuleTargetMode;
  fixedAmountMinor: number | null;
  percentBp: number | null;
  sortOrder: number;
}

export interface AllocationRule {
  id: number;
  accountId: number;
  name: string;
  enabled: boolean;
  priority: number;
  matchPayeeContains: string | null;
  matchPayeeRegex: string | null;
  matchAmountMinMinor: number | null;
  matchAmountMaxMinor: number | null;
  targets: RuleTarget[];
  /** When set, the rule classifies as a transfer instead of allocating (TMMAPP-0026). */
  transferAccountId: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface RuleDraft {
  name: string;
  enabled: boolean;
  matchPayeeContains?: string;
  matchPayeeRegex?: string;
  matchAmountMinMinor?: number;
  matchAmountMaxMinor?: number;
  targets: Array<Omit<RuleTarget, 'id'>>;
  transferAccountId?: number | null;
}

/**
 * What an edited rule changed (TMMAPP-0134): a cleared filter or transfer account is `null`
 * (TMMAPI-0169: "`null` clears it"); an absent key is unchanged.
 */
export interface RulePatch {
  name?: string;
  enabled?: boolean;
  matchPayeeContains?: string | null;
  matchPayeeRegex?: string | null;
  matchAmountMinMinor?: number | null;
  matchAmountMaxMinor?: number | null;
  targets?: Array<Omit<RuleTarget, 'id'>>;
  transferAccountId?: number | null;
}

export interface DryRunMatch {
  transactionId: number;
  postedOn: string;
  payee: string;
  amountMinor: number;
  wouldAllocate: Array<{
    envelopeId: number;
    categoryId: number | null;
    amountMinor: number;
  }>;
  shortfallMinor: number;
}

export interface DryRun {
  consideredCount: number;
  matchedCount: number;
  matched: DryRunMatch[];
}

const MODES: ReadonlySet<string> = new Set(['FIXED', 'PERCENT']);

/** A target; `null` (dropped) without an envelope or for a mode this build doesn't know. */
function normalizeTarget(w: WireTarget): RuleTarget | null {
  if (w.envelopeId === undefined) return null;
  if (!MODES.has(w.mode)) return null;
  return {
    id: w.id,
    envelopeId: w.envelopeId,
    categoryId: w.categoryId ?? null,
    mode: w.mode,
    fixedAmountMinor: w.fixedAmountMinor ?? null,
    percentBp: w.percentBp ?? null,
    sortOrder: w.sortOrder,
  };
}

export function normalizeRule(w: AllocationRuleResponse): AllocationRule {
  const targets = w.targets.flatMap((row) => {
    const target = normalizeTarget(row);
    return target ? [target] : [];
  });
  targets.sort((a, b) => a.sortOrder - b.sortOrder || a.id - b.id);
  return {
    id: w.id,
    accountId: w.accountId,
    name: w.name,
    enabled: w.enabled,
    priority: w.priority,
    matchPayeeContains: w.matchPayeeContains ?? null,
    matchPayeeRegex: w.matchPayeeRegex ?? null,
    matchAmountMinMinor: w.matchAmountMinMinor ?? null,
    matchAmountMaxMinor: w.matchAmountMaxMinor ?? null,
    targets,
    transferAccountId: w.transferAccountId ?? null,
    createdAt: w.createdAt,
    updatedAt: w.updatedAt,
  };
}

export function normalizeRules(value: readonly AllocationRuleResponse[]): AllocationRule[] {
  return value.map(normalizeRule).sort((a, b) => a.priority - b.priority || a.id - b.id);
}

function targetRequest(target: Omit<RuleTarget, 'id'>, index: number): AllocationRuleTargetRequest {
  const wire: AllocationRuleTargetRequest = {
    envelopeId: target.envelopeId,
    mode: target.mode,
    sortOrder: index,
  };
  if (target.categoryId !== null) wire.categoryId = target.categoryId;
  if (target.mode === 'FIXED' && target.fixedAmountMinor !== null) {
    wire.fixedAmountMinor = target.fixedAmountMinor;
  }
  if (target.mode === 'PERCENT' && target.percentBp !== null) wire.percentBp = target.percentBp;
  return wire;
}

function optionalString(value: string | undefined): string | undefined {
  if (value === undefined) return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

/** Create/dry-run body: empty strings and unused match keys are not sent. `sortOrder = index`. */
export function draftToCreateRequest(draft: RuleDraft): AllocationRuleCreateRequest {
  const transferAccountId = draft.transferAccountId ?? undefined;
  const body: AllocationRuleCreateRequest = {
    name: draft.name,
    enabled: draft.enabled,
    targets:
      transferAccountId !== undefined
        ? []
        : draft.targets.map((target, index) => targetRequest(target, index)),
  };
  if (transferAccountId !== undefined) body.transferAccountId = transferAccountId;
  const contains = optionalString(draft.matchPayeeContains);
  if (contains !== undefined) body.matchPayeeContains = contains;
  const regex = optionalString(draft.matchPayeeRegex);
  if (regex !== undefined) body.matchPayeeRegex = regex;
  if (draft.matchAmountMinMinor !== undefined) body.matchAmountMinMinor = draft.matchAmountMinMinor;
  if (draft.matchAmountMaxMinor !== undefined) body.matchAmountMaxMinor = draft.matchAmountMaxMinor;
  return body;
}

/** A cleared text filter: `null`, or a blank string (which the contract also reads as a clear). */
function patchString(value: string | null | undefined): string | null | undefined {
  if (value === undefined || value === null) return value;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * Patch: only the keys given are sent; an omitted key keeps the stored value, and `null` clears a
 * filter (TMMAPI-0169). Turning a transfer rule back into an allocating one sends
 * `transferAccountId: null` with the targets in the same request, as the contract requires.
 */
export function draftToPatchRequest(patch: RulePatch): AllocationRulePatchRequest {
  const body: AllocationRulePatchRequest = {};
  if (patch.name !== undefined) body.name = patch.name;
  if (patch.enabled !== undefined) body.enabled = patch.enabled;
  const contains = patchString(patch.matchPayeeContains);
  if (contains !== undefined) body.matchPayeeContains = contains;
  const regex = patchString(patch.matchPayeeRegex);
  if (regex !== undefined) body.matchPayeeRegex = regex;
  if (patch.matchAmountMinMinor !== undefined) body.matchAmountMinMinor = patch.matchAmountMinMinor;
  if (patch.matchAmountMaxMinor !== undefined) body.matchAmountMaxMinor = patch.matchAmountMaxMinor;
  if (patch.transferAccountId !== undefined && patch.transferAccountId !== null) {
    body.transferAccountId = patch.transferAccountId;
    body.targets = [];
    return body;
  }
  if (patch.transferAccountId === null) body.transferAccountId = null;
  if (patch.targets !== undefined) {
    body.targets = patch.targets.map((target, index) => targetRequest(target, index));
  }
  return body;
}

const sameTargets = (a: ReadonlyArray<Omit<RuleTarget, 'id'>>, b: ReadonlyArray<RuleTarget>) =>
  a.length === b.length &&
  a.every((target, index) => {
    const other = b[index]!;
    return (
      target.envelopeId === other.envelopeId &&
      target.categoryId === other.categoryId &&
      target.mode === other.mode &&
      target.fixedAmountMinor === other.fixedAmountMinor &&
      target.percentBp === other.percentBp
    );
  });

/**
 * What an edited rule changed: the PATCH sends only these keys (TMMAPP-0134). A filter the draft
 * no longer has is `null`, a clear (TMMAPP-0136 item 10).
 */
export function ruleDraftChanges(rule: AllocationRule, draft: RuleDraft): RulePatch {
  const patch: RulePatch = {};
  if (draft.name !== rule.name) patch.name = draft.name;
  if (draft.enabled !== rule.enabled) patch.enabled = draft.enabled;
  const contains = draft.matchPayeeContains ?? null;
  if (contains !== rule.matchPayeeContains) patch.matchPayeeContains = contains;
  const regex = draft.matchPayeeRegex ?? null;
  if (regex !== rule.matchPayeeRegex) patch.matchPayeeRegex = regex;
  const min = draft.matchAmountMinMinor ?? null;
  if (min !== rule.matchAmountMinMinor) patch.matchAmountMinMinor = min;
  const max = draft.matchAmountMaxMinor ?? null;
  if (max !== rule.matchAmountMaxMinor) patch.matchAmountMaxMinor = max;
  const transfer = draft.transferAccountId ?? null;
  if (transfer !== rule.transferAccountId) patch.transferAccountId = transfer;
  if (
    transfer === null &&
    (rule.transferAccountId !== null || !sameTargets(draft.targets, rule.targets))
  ) {
    patch.targets = draft.targets;
  }
  return patch;
}

function normalizeMatch(w: WireDryRunMatch): DryRunMatch {
  return {
    transactionId: w.transactionId,
    postedOn: w.postedOn,
    payee: w.payee,
    amountMinor: w.amountMinor,
    wouldAllocate: w.wouldAllocate.flatMap((row) =>
      row.envelopeId === undefined
        ? []
        : [
            {
              envelopeId: row.envelopeId,
              categoryId: row.categoryId ?? null,
              amountMinor: row.amountMinor,
            },
          ],
    ),
    shortfallMinor: w.shortfallMinor,
  };
}

export function normalizeDryRun(w: AllocationRuleDryRunResponse): DryRun {
  return {
    consideredCount: w.consideredCount,
    matchedCount: w.matchedCount,
    matched: w.matched.map(normalizeMatch),
  };
}

/** `RULE_INVALID` field map (`details.backend.fields`); empty for any other error. */
export function ruleInvalidFields(error: unknown): Record<string, string> {
  if (!isApiError(error) || error.code !== 'RULE_INVALID') return {};
  return backendFields(error);
}

const base = (accountId: number) => `/v1/me/accounts/${accountId}/allocation-rules`;

export const allocationRulesApi = {
  async list(accountId: number): Promise<AllocationRule[]> {
    return normalizeRules(await request<AllocationRuleResponse[]>('GET', base(accountId)));
  },
  async get(accountId: number, ruleId: number): Promise<AllocationRule> {
    return normalizeRule(
      await request<AllocationRuleResponse>('GET', `${base(accountId)}/${ruleId}`),
    );
  },
  async create(accountId: number, draft: RuleDraft): Promise<AllocationRule> {
    return normalizeRule(
      await request<AllocationRuleResponse>('POST', base(accountId), {
        json: draftToCreateRequest(draft),
      }),
    );
  },
  async patch(accountId: number, ruleId: number, patch: RulePatch): Promise<AllocationRule> {
    return normalizeRule(
      await request<AllocationRuleResponse>('PATCH', `${base(accountId)}/${ruleId}`, {
        json: draftToPatchRequest(patch),
      }),
    );
  },
  async remove(accountId: number, ruleId: number): Promise<void> {
    await request<void>('DELETE', `${base(accountId)}/${ruleId}`);
  },
  async reorder(accountId: number, ruleIds: number[]): Promise<AllocationRule[]> {
    return normalizeRules(
      await request<AllocationRuleResponse[]>('POST', `${base(accountId)}/reorder`, {
        json: { ruleIds },
      }),
    );
  },
  async dryRun(
    accountId: number,
    body: { rule: RuleDraft } | { ruleId: number },
    range: { from: string; to: string },
  ): Promise<DryRun> {
    const json =
      'ruleId' in body
        ? { ruleId: body.ruleId, from: range.from, to: range.to }
        : { rule: draftToCreateRequest(body.rule), from: range.from, to: range.to };
    return normalizeDryRun(
      await request<AllocationRuleDryRunResponse>('POST', `${base(accountId)}/dry-run`, { json }),
    );
  },
};
